import { useRef, useEffect, useState, createContext, useContext, type ReactNode } from 'react';
import type { Channel, VoiceParticipant } from '../types';
import { useApp } from '../store';
import { Avatar, Button, Icon } from './ui';
import { UserClickTarget } from './UserPopover';
import { useVoice, type VoiceApi } from './voiceContext';
import { useIsServerMuted } from '../voiceMutes';
import { useVideoDevices } from '../hooks';
import { playSound } from '../sounds';

// Whether this device uses a coarse pointer (touch) — used to gate the
// front/back camera flip button to phones/tablets. Computed once; pointer type
// effectively never changes within a session.
const COARSE_POINTER =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

// -----------------------------------------------------------------------------
// Guest mode
// -----------------------------------------------------------------------------
// In an instant meeting room (screens/MeetRoomScreen) the viewer may have no
// account at all. Every participant tile and speaker chip is wrapped in a
// `UserClickTarget`, which opens a popover offering Add Friend / Message /
// View Profile — all meaningless without a session, and it does NOT disable
// itself when `user` is null. Rather than drill a prop through RoomGrid into
// both tile components, guest mode rides a context read at the two wrap sites.
const GuestModeContext = createContext(false);

/** Wraps children in UserClickTarget, except in a guest meeting room. */
function MaybeUserTarget({
  children,
  ...targetProps
}: {
  children: ReactNode;
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  className?: string;
}) {
  const guestMode = useContext(GuestModeContext);
  if (guestMode) {
    return <div className={targetProps.className}>{children}</div>;
  }
  return <UserClickTarget {...targetProps}>{children}</UserClickTarget>;
}

interface Props {
  channel: Channel;
  serverId: string;
  // Live participant list from useServerVoiceState — used on the join
  // pre-screen to show who's already in the room.
  preloadedParticipants?: VoiceParticipant[];
  // Optional side panel docked next to the call, toggled from the header.
  // Deliberately a ReactNode rather than a specific channel/thread type: a
  // meeting room passes <MeetChatPanel>, and a DM call can pass
  // <TextChannelView> into the same slot without either knowing about the
  // other.
  chatPanel?: ReactNode;
  // Render for a viewer who may not be signed in — suppresses the per-tile
  // user popover (friend/DM/profile actions need a session).
  guestMode?: boolean;
  // Replaces the built-in "Join Voice" pre-screen. Meeting rooms run their own
  // name-entry + device preflight before connecting.
  hideJoinScreen?: boolean;
}

export function VoiceChannelView({
  channel,
  serverId,
  preloadedParticipants,
  chatPanel,
  guestMode = false,
  hideJoinScreen = false,
}: Props) {
  const setActiveVoice = useApp((s) => s.setActiveVoice);
  const activeVoice = useApp((s) => s.activeVoice);
  const voice = useVoice();

  // Whether the docked chat panel is open. Harmless when `chatPanel` is absent.
  const [chatOpen, setChatOpen] = useState(false);

  // We're "in this room" if the active voice channel matches AND voice is
  // actually connected (LiveKit connected, not just token requested).
  const inThisRoom = voice.inRoom && activeVoice?.channelId === channel.id;

  function joinThisChannel() {
    // Play the join cue at the moment of the click rather than relying on
    // LiveKit's onConnected callback. The callback can fire after the
    // AudioContext has briefly suspended (page focus loss during connect)
    // or after a navigation, both of which can swallow the cue. Playing
    // at click guarantees the user always hears it.
    if (useApp.getState().soundsEnabled) playSound('join');
    setActiveVoice({
      serverId,
      channelId: channel.id,
      channelName: channel.name,
    });
  }

  // Header is shared between join screen and connected screen. When a chat
  // panel is supplied it carries the toggle on the right — mirroring the text
  // view's header rather than crowding the bottom call controls.
  const header = (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-surface shrink-0">
      <Icon name="speaker" size={14} className="text-text-muted" />
      <h2 className="font-bold text-text">{channel.name}</h2>
      {channel.topic && (
        <>
          <span className="text-text-subtle px-1">|</span>
          <span className="text-xs text-text-muted truncate">{channel.topic}</span>
        </>
      )}
      {chatPanel && inThisRoom && (
        <button
          type="button"
          onClick={() => setChatOpen((o) => !o)}
          title={chatOpen ? 'Hide chat' : 'Show chat'}
          aria-label={chatOpen ? 'Hide chat' : 'Show chat'}
          aria-pressed={chatOpen}
          className={`ml-auto h-8 min-h-[32px] inline-flex items-center gap-1.5 px-2 rounded-aim text-sm font-semibold transition-colors ${
            chatOpen
              ? 'bg-accent text-accent-text'
              : 'text-text-subtle hover:text-text hover:bg-elevated'
          }`}
        >
          <Icon name="chat" size={16} />
          <span className="hidden sm:inline">Chat</span>
        </button>
      )}
    </div>
  );

  if (inThisRoom) {
    return (
      <GuestModeContext.Provider value={guestMode}>
        {/* Horizontal split: the call column on the left, and — when toggled —
            the chat panel docked on the right (desktop) or as a full overlay
            over the call (mobile, where a 320px dock would leave nothing). */}
        <div className="relative flex h-full min-h-0">
          {/* min-w-0: a flex item's min width defaults to its min-content, so a
              wide descendant (a share pane, a fixed-width enlarged cam) would
              force this column past a phone's viewport and the room — incl.
              the control bar — got clipped on the right. Same class of bug as
              the chat column's (see CLAUDE.md "Long-token wrapping"). */}
          <div className="flex flex-col flex-1 min-h-0 min-w-0">
            {header}
            <RoomGrid api={voice} />
          </div>
          {chatPanel && chatOpen && (
            <div className="absolute inset-0 z-30 flex flex-col bg-surface md:static md:inset-auto md:z-auto md:w-80 lg:w-96 md:shrink-0 md:border-l md:border-border">
              {chatPanel}
            </div>
          )}
        </div>
      </GuestModeContext.Provider>
    );
  }

  // DM call, or a meeting room, that hasn't finished connecting yet — show a
  // brief "Connecting…" state rather than the server-style "Join Voice"
  // pre-screen. A meeting room has already run its own name entry and device
  // preflight by this point, so offering "Join Voice" again would be confusing.
  const isDmCall = !!activeVoice?.isDm && activeVoice.channelId === channel.id;
  if (isDmCall || hideJoinScreen) {
    return (
      <div className="flex flex-col h-full min-h-0">
        {header}
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 bg-bg text-text-muted">
          <Icon name="phone" size={28} className="animate-pulse" />
          <div className="text-sm">Connecting…</div>
        </div>
      </div>
    );
  }

  // Not connected to this channel — show join screen with current participants
  const participants = preloadedParticipants ?? [];
  const inOtherChannel = activeVoice && activeVoice.channelId !== channel.id;

  return (
    <div className="flex flex-col h-full min-h-0">
      {header}
      <div className="flex-1 min-h-0 flex items-center justify-center p-6 bg-bg overflow-y-auto">
        <div className="hybrid-card w-full max-w-md">
          <div className="hybrid-card-header">Voice channel</div>
          <div className="p-6 bg-surface space-y-4">
            <div className="text-center">
              <Icon name="speaker" size={32} className="mx-auto mb-2 text-text-muted" />
              <h3 className="text-lg font-bold text-text">{channel.name}</h3>
              <div className="text-xs text-text-muted">
                {participants.length === 0
                  ? 'No one is here yet — be the first!'
                  : `${participants.length} ${participants.length === 1 ? 'person' : 'people'} here`}
              </div>
            </div>

            {participants.length > 0 && (
              <div className="bevel-down rounded-aim p-3 bg-elevated">
                <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
                  In Voice
                </div>
                <div className="space-y-1">
                  {participants.map((p) => {
                    const name = p.displayName || p.username;
                    return (
                      <div
                        key={p.userId}
                        className="flex items-center gap-2 text-sm text-text"
                      >
                        <Avatar name={name} url={p.avatarUrl} size={20} />
                        <span>{name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {inOtherChannel && (
              <div className="bevel-down rounded-aim bg-accent/10 p-2 text-xs text-text">
                You're currently in <strong>#{activeVoice.channelName}</strong>.
                Joining here will move you.
              </div>
            )}

            <Button variant="primary" fullWidth onClick={joinThisChannel}>
              <Icon name="phone" size={14} />
              {inOtherChannel ? `Switch to #${channel.name}` : 'Join Voice'}
            </Button>

            <p className="text-[10px] text-text-subtle text-center">
              Your browser will ask for microphone access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Control-bar buttons: below the `sm` breakpoint they're icon-only and the
// default aim-button padding leaves them ~32px tall — under Apple's 44px
// touch-target guideline. Enforce 44px on phones; revert to normal at sm+.
const CTRL_BTN = 'min-h-[44px] min-w-[48px] inline-flex items-center justify-center gap-1.5 rounded-full! sm:min-h-0 sm:min-w-0 sm:px-4! sm:py-2!';

// RoomGrid — the participant grid + controls when connected to THIS room
// =============================================================================
// ParticipantTile — one voice participant. Renders their live camera feed when
// they have one published, otherwise their avatar. The local feed is mirrored
// (like every video app) and muted so we don't echo ourselves. Speaking ring
// and mute badge overlay either state.
//
// Tiles live in a flex-wrap gallery, so "enlarging" a feed is just a bigger
// width on that one tile — the others reflow around it. This works the same
// whether or not a screen share is present (the tiles are simply more compact
// in share mode), so an enlarged webcam coexists cleanly with shares + cams.
// =============================================================================
// ConnectionQualityBadge — small bottom-left badge on a participant tile,
// shown only when the LiveKit server reports their network as degraded
// (poor = amber, lost = red). Renders nothing for good/excellent/unknown so
// the common case stays clean; the point is diagnosability ("Bob sounds
// robotic" → glance at the grid → it's Bob's connection, not the server).
// Colors are fixed (not theme vars) like the speaking ring — status colors
// must read the same on every theme.
// =============================================================================
function ConnectionQualityBadge({
  quality,
  displayName,
  isLocal,
  compact,
}: {
  quality: ReturnType<VoiceApi['getConnectionQuality']>;
  displayName: string;
  isLocal: boolean;
  compact?: boolean;
}) {
  if (quality !== 'poor' && quality !== 'lost') return null;
  const lost = quality === 'lost';
  const who = isLocal ? 'Your' : `${displayName}'s`;
  return (
    <div
      className={`absolute bottom-1 left-1 rounded-full border-2 border-surface ${
        lost ? 'bg-danger' : 'bg-[#D97706]'
      } ${compact ? 'p-0.5' : 'p-1'}`}
      title={lost ? `${who} connection was lost` : `${who} connection is poor`}
    >
      <Icon name="signal" size={compact ? 8 : 12} className="text-white" />
    </div>
  );
}

function ParticipantTile({
  p,
  api,
  hasShare,
  tileCount,
  isFocused,
  onToggleFocus,
}: {
  p: VoiceApi['participants'][number];
  api: VoiceApi;
  hasShare: boolean;
  /** Number of participants in the room. Drives headcount-based tile sizing
   *  when there's no screen share: a solo/pair call gets large tiles, a full
   *  room compacts. */
  tileCount: number;
  isFocused: boolean;
  onToggleFocus: (identity: string) => void;
}) {
  const meta = parseMetadata(p.metadata);
  const displayName = meta.displayName || meta.username || p.identity;
  const isLocal = p.identity === api.localParticipantIdentity;
  const speaking = api.isSpeaking(p.identity);
  // api.isMuted() returns true for participants who haven't published a mic
  // track yet — "muted enough" for this indicator.
  const isMuted = api.isMuted(p.identity);
  // Moderator mute: same badge, amber instead of red, so it reads differently.
  const serverMuted = useIsServerMuted(useApp((s) => s.activeVoice?.serverId), p.identity);

  const camPub = api.getCameraPublication(p.identity);
  const camTrack = camPub && !camPub.isMuted ? camPub.videoTrack ?? camPub.track : undefined;
  // Local cam is always renderable; remote needs an active subscription (camera
  // is auto-subscribed, so this is true once tracks settle).
  const showCam = !!camTrack && (isLocal || !!camPub?.isSubscribed);

  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !camTrack || !showCam) return;
    camTrack.attach(el);
    return () => {
      camTrack.detach(el);
    };
  }, [camTrack, showCam]);

  // Only camera tiles can be enlarged (no point blowing up an avatar).
  const expandable = showCam;
  const enlarged = isFocused && showCam;
  // Tile width. Enlarged tiles get a much larger footprint; the flex-wrap
  // container reflows the rest around them. When a share is on, tiles are a
  // compact strip (the share is the focus). Otherwise size by headcount so a
  // solo/pair call fills the space and a full room compacts gracefully.
  // The 20rem sizes are w-full + max-w so they shrink on a phone narrower than
  // 20rem + the grid's padding instead of overflowing it.
  const widthClass = enlarged
    ? hasShare
      ? 'w-72 sm:w-96'
      : 'w-full max-w-[20rem] sm:w-120 sm:max-w-none lg:w-160'
    : hasShare
      ? 'w-28'
      : tileCount <= 1
        ? 'w-full max-w-[20rem] sm:w-md sm:max-w-none'
        : tileCount === 2
          ? 'w-72 sm:w-80'
          : tileCount <= 4
            ? 'w-52 sm:w-60'
            : tileCount <= 6
              ? 'w-44 sm:w-52'
              : 'w-40 sm:w-44';
  // Avatar scales with the tile so it doesn't look lost in a big solo tile or
  // oversized in a crowded grid. (When a share is on, tiles are tiny — keep 32.)
  const avatarSize = hasShare
    ? 32
    : tileCount <= 1
      ? 112
      : tileCount === 2
        ? 88
        : tileCount <= 4
          ? 64
          : tileCount <= 6
            ? 56
            : 48;

  return (
    <MaybeUserTarget
      userId={p.identity}
      username={meta.username || p.identity}
      displayName={meta.displayName ?? null}
      avatarUrl={meta.avatarUrl ?? null}
      className={`flex flex-col items-center gap-1.5 shrink-0 ${enlarged ? 'order-first' : ''}`}
    >
      <div
        className={`hybrid-tile group relative overflow-hidden flex items-center justify-center aspect-video ${widthClass} ${
          speaking
            ? 'border-online! shadow-[0_0_0_3px_rgba(91,191,91,0.35),0_4px_14px_rgba(0,0,0,0.12)]'
            : 'hover:-translate-y-0.5 hover:shadow-[0_10px_22px_rgba(0,0,0,0.13)]'
        }`}
      >
        {showCam ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isLocal /* never feed our own audioless cam back, and avoids autoplay blocks */}
            className={`w-full h-full object-cover ${isLocal ? 'scale-x-[-1]' : ''}`}
          />
        ) : (
          <Avatar name={displayName} url={meta.avatarUrl} size={avatarSize} />
        )}
        {/* Enlarge / shrink toggle. A div (not button) because this tile is
            wrapped in UserClickTarget's <button>, and nested buttons are
            invalid HTML. stopPropagation keeps the click from also opening the
            user popover. */}
        {expandable && (
          <div
            role="button"
            tabIndex={0}
            aria-label={enlarged ? `Shrink ${displayName}'s camera` : `Enlarge ${displayName}'s camera`}
            title={enlarged ? 'Shrink' : 'Enlarge'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFocus(p.identity);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onToggleFocus(p.identity);
              }
            }}
            className={`absolute top-1 right-1 rounded bg-black/50 text-white transition-opacity cursor-pointer focus:opacity-100 ${
              COARSE_POINTER ? 'p-2' : 'p-1'
            } ${
              // Touch devices have no hover, so keep it visible there (otherwise
              // the enlarge affordance is unreachable on a phone).
              enlarged || COARSE_POINTER ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <Icon name={enlarged ? 'minimize' : 'expand'} size={hasShare && !enlarged ? 11 : 14} />
          </div>
        )}
        {isMuted && (
          <div
            className={`absolute bottom-1 right-1 ${serverMuted ? 'bg-[#D97706]' : 'bg-danger'} rounded-full ${hasShare && !enlarged ? 'p-0.5' : 'p-1'} border-2 border-surface`}
            title={serverMuted ? (isLocal ? 'A moderator muted you' : `${displayName} was muted by a moderator`) : isLocal ? 'You are muted' : `${displayName} is muted`}
          >
            <Icon name="mic-off" size={hasShare && !enlarged ? 8 : 12} className="text-white" />
          </div>
        )}
        <ConnectionQualityBadge
          quality={api.getConnectionQuality(p.identity)}
          displayName={displayName}
          isLocal={isLocal}
          compact={hasShare && !enlarged}
        />
      </div>
      <div
        className={`${
          hasShare && !enlarged ? 'text-[10px]' : 'text-sm'
        } font-medium text-text text-center max-w-[90%] truncate`}
      >
        {displayName}
        {isLocal && <span className="text-text-subtle text-xs ml-1">(you)</span>}
      </div>
    </MaybeUserTarget>
  );
}

// =============================================================================
// SpeakerChip — compact SQUARE tile used when NOBODY is on camera (the common
// voice-only call). Same bordered/ring look as ParticipantTile for consistency,
// but square + small with the name below, so a camera-off call reads as a tidy
// roster rather than a grid of empty 16:9 frames. The moment anyone enables a
// camera the grid switches back to ParticipantTile video tiles.
// =============================================================================
function SpeakerChip({
  p,
  api,
}: {
  p: VoiceApi['participants'][number];
  api: VoiceApi;
}) {
  const meta = parseMetadata(p.metadata);
  const displayName = meta.displayName || meta.username || p.identity;
  const isLocal = p.identity === api.localParticipantIdentity;
  const speaking = api.isSpeaking(p.identity);
  const isMuted = api.isMuted(p.identity);
  // Moderator mute: same badge, amber instead of red, so it reads differently.
  const serverMuted = useIsServerMuted(useApp((s) => s.activeVoice?.serverId), p.identity);

  return (
    <MaybeUserTarget
      userId={p.identity}
      username={meta.username || p.identity}
      displayName={meta.displayName ?? null}
      avatarUrl={meta.avatarUrl ?? null}
      className="flex flex-col items-center gap-1.5 shrink-0"
    >
      <div
        className={`hybrid-tile group relative overflow-hidden flex items-center justify-center aspect-square w-28 sm:w-32 ${
          speaking
            ? 'border-online! shadow-[0_0_0_3px_rgba(91,191,91,0.35),0_4px_14px_rgba(0,0,0,0.12)]'
            : 'hover:-translate-y-0.5 hover:shadow-[0_10px_22px_rgba(0,0,0,0.13)]'
        }`}
      >
        <Avatar name={displayName} url={meta.avatarUrl} size={64} />
        {isMuted && (
          <div
            className={`absolute bottom-1 right-1 ${serverMuted ? 'bg-[#D97706]' : 'bg-danger'} rounded-full p-1 border-2 border-surface`}
            title={serverMuted ? (isLocal ? 'A moderator muted you' : `${displayName} was muted by a moderator`) : isLocal ? 'You are muted' : `${displayName} is muted`}
          >
            <Icon name="mic-off" size={12} className="text-white" />
          </div>
        )}
        <ConnectionQualityBadge
          quality={api.getConnectionQuality(p.identity)}
          displayName={displayName}
          isLocal={isLocal}
        />
      </div>
      <div className="text-sm font-medium text-text text-center max-w-[90%] truncate">
        {displayName}
        {isLocal && <span className="text-text-subtle text-xs ml-1">(you)</span>}
      </div>
    </MaybeUserTarget>
  );
}

function RoomGrid({ api }: { api: VoiceApi }) {
  // If anyone is screen-sharing, show their feed prominently and demote the
  // participant tiles to a small strip below.
  const hasShare = api.screenShares.length > 0;
  // Identity of the camera currently enlarged inline (null = none). The tile
  // itself grows; the flex-wrap gallery reflows the others around it.
  const [focusedCamera, setFocusedCamera] = useState<string | null>(null);
  const toggleFocus = (identity: string) =>
    setFocusedCamera((cur) => (cur === identity ? null : identity));

  // Drop the focus if that camera goes away (turned off / participant left) so
  // we don't keep an empty enlarged slot.
  useEffect(() => {
    if (!focusedCamera) return;
    const pub = api.getCameraPublication(focusedCamera);
    if (!pub || pub.isMuted) setFocusedCamera(null);
  }, [focusedCamera, api]);

  // Flip (front/back) is only meaningful on a device with multiple cameras —
  // i.e. a phone/tablet. Gate on a coarse pointer so a desktop with two webcams
  // doesn't get a confusing button (use the settings device picker there).
  const cameras = useVideoDevices().cameras;
  const canFlipCamera = api.isCameraOn && COARSE_POINTER && cameras.length >= 2;

  // Is anyone actually showing a camera feed? (local: published & unmuted;
  // remote: also subscribed — cameras auto-subscribe so this settles quickly.)
  const hasCam = (identity: string): boolean => {
    const pub = api.getCameraPublication(identity);
    if (!pub || pub.isMuted) return false;
    return identity === api.localParticipantIdentity || !!pub.isSubscribed;
  };
  const anyCamera = api.participants.some((p) => hasCam(p.identity));
  // Voice-only: no share AND no cameras → compact speaker chips.
  const voiceOnly = !hasShare && !anyCamera;

  const tiles = api.participants.map((p) => (
    <ParticipantTile
      key={p.identity}
      p={p}
      api={api}
      hasShare={hasShare}
      tileCount={api.participants.length}
      isFocused={focusedCamera === p.identity}
      onToggleFocus={toggleFocus}
    />
  ));

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {hasShare ? (
        // Share mode fits the viewport (no outer scroll): the share takes the
        // flexible top region (scrolls internally if it's taller than the
        // space), and the participant tiles are a fixed-height bottom strip
        // that scrolls sideways. Enlarging a cam grows the strip and shrinks
        // the share to make room — so the enlarged cam is always fully visible
        // without having to scroll the whole view down to find it.
        <div className="flex-1 min-h-0 bg-bg flex flex-col gap-3 p-3">
          <div className="flex-1 min-h-0 flex flex-col">
            <ScreenShareStage
              shares={api.screenShares}
              participants={api.participants}
              api={api}
            />
          </div>
          <div className="shrink-0 overflow-x-auto -mx-1">
            <div className="flex flex-nowrap items-center gap-3 w-max mx-auto px-1">{tiles}</div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto bg-bg p-4 sm:p-6">
          {/* min-h-full + justify-center vertically centers the gallery when it
              fits the viewport (instead of pinning it to the top over a sea of
              empty background); when it's taller it grows and scrolls as before. */}
          <div className="min-h-full flex flex-col justify-center">
            {voiceOnly ? (
              // Voice-only roster: compact square tiles, wrap + center.
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 max-w-3xl mx-auto">
                {api.participants.map((p) => (
                  <SpeakerChip key={p.identity} p={p} api={api} />
                ))}
              </div>
            ) : (
              // Camera grid: headcount-sized video tiles, wrap + center.
              <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto w-full">{tiles}</div>
            )}
          </div>
        </div>
      )}

      {/* Control bar. The buttons live in a single floating, elevated pill
          cluster (hybrid-ctrl-group) over the room background rather than a
          flat docked strip. Labels collapse to icon-only on narrow screens so
          the full set (incl. leave) always fits a phone; flex-wrap is a safety
          net if it ever still overflows. */}
      <div className="bg-bg px-3 pt-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center justify-center shrink-0">
       <div className="hybrid-ctrl-group flex-wrap justify-center">
        <Button className={CTRL_BTN} onClick={api.toggleMute} variant={api.muted ? 'danger' : 'default'} title={api.serverMuted ? 'Muted by a moderator' : api.muted ? 'Unmute' : 'Mute'}>
          <Icon name={api.muted ? 'mic-off' : 'mic'} size={16} />
          <span className="hidden sm:inline">{api.muted ? 'Muted' : 'Mic On'}</span>
        </Button>
        <Button
          className={CTRL_BTN}
          onClick={api.toggleCamera}
          variant={api.isCameraOn ? 'primary' : 'default'}
          title={api.isCameraOn ? 'Turn camera off' : 'Turn camera on'}
        >
          <Icon name={api.isCameraOn ? 'video-off' : 'video'} size={16} />
          <span className="hidden sm:inline">{api.isCameraOn ? 'Camera On' : 'Camera'}</span>
        </Button>
        {canFlipCamera && (
          <Button className={CTRL_BTN} onClick={api.flipCamera} title="Switch front/back camera">
            <Icon name="flip-camera" size={16} />
          </Button>
        )}
        <Button
          className={CTRL_BTN}
          onClick={api.toggleScreenShare}
          variant={api.isSharingScreen ? 'primary' : 'default'}
          title={api.isSharingScreen ? 'Stop sharing' : 'Share your screen'}
        >
          <Icon name={api.isSharingScreen ? 'monitor-off' : 'monitor'} size={16} />
          <span className="hidden sm:inline">{api.isSharingScreen ? 'Stop sharing' : 'Share screen'}</span>
        </Button>
        <Button className={CTRL_BTN} onClick={api.leave} variant="danger" title="Leave voice">
          <Icon name="phone-off" size={16} />
          <span className="hidden sm:inline">Leave</span>
        </Button>
       </div>
      </div>
    </div>
  );
}

// =============================================================================
// ScreenShareStage — renders screen-share entries with opt-in subscription
// =============================================================================
// Each share is a card. If you've opted in to watch (subscribed), the video
// renders. If you haven't, you see a prominent click-to-watch card (sharer
// avatar + name + a "Watch screen" button); the whole card is the hit target.
// The share stays unsubscribed until clicked, so it costs no bandwidth until
// you actually want to look. A "pop out" button on each subscribed share opens
// the floating viewer that persists across navigation.
// =============================================================================
function ScreenShareStage({
  shares,
  participants,
  api,
}: {
  shares: VoiceApi['screenShares'];
  participants: VoiceApi['participants'];
  api: VoiceApi;
}) {
  const remoteShares = shares.filter(
    (s) => s.participantIdentity !== api.localParticipantIdentity
  );
  const ownShare = shares.find(
    (s) => s.participantIdentity === api.localParticipantIdentity
  );

  function nameFor(identity: string): string {
    const p = participants.find((x) => x.identity === identity);
    if (!p) return identity;
    const meta = parseMetadata(p.metadata);
    return meta.displayName || meta.username || identity;
  }

  function avatarFor(identity: string): string | null {
    const p = participants.find((x) => x.identity === identity);
    return parseMetadata(p?.metadata).avatarUrl ?? null;
  }

  // Resolve the list of people watching a given share into display info.
  // getWatchers() returns only remote watchers; for a remote share we're also
  // watching, prepend ourselves ("You") so the count matches reality.
  function watchersFor(
    sharerIdentity: string
  ): Array<{ identity: string; name: string; avatarUrl: string | null }> {
    const ids = [...api.getWatchers(sharerIdentity)];
    const isOwn = sharerIdentity === api.localParticipantIdentity;
    const share = shares.find((s) => s.participantIdentity === sharerIdentity);
    const self = api.localParticipantIdentity;
    if (!isOwn && share?.isSubscribed && self && !ids.includes(self)) {
      ids.unshift(self);
    }
    return ids.map((identity) => {
      const p = participants.find((x) => x.identity === identity);
      const meta = parseMetadata(p?.metadata);
      return {
        identity,
        name: identity === self ? 'You' : meta.displayName || meta.username || identity,
        avatarUrl: meta.avatarUrl ?? null,
      };
    });
  }

  const visibleCards = ownShare
    ? [ownShare, ...remoteShares]
    : remoteShares;
  const single = visibleCards.length === 1;

  return (
    // Single share fills the available height (so it scales down reactively as
    // the participant strip grows — e.g. when a webcam is enlarged — instead of
    // becoming an internal scroll area). Multiple shares fall back to a 2-col
    // grid that scrolls if needed.
    <div
      className={
        single
          ? // justify-center vertically centers an unwatched (content-height)
            // card in the stage; a watched share is flex-1 so it's a no-op.
            'flex-1 min-h-0 w-full max-w-5xl mx-auto flex flex-col justify-center'
          : // content-start keeps rows packed to the top at their content height
            // (otherwise grid stretches the row to fill, leaving each pane tall
            // with the video only in its top half). Scrolls if cards overflow.
            'flex-1 min-h-0 overflow-y-auto content-start grid grid-cols-1 md:grid-cols-2 gap-3 max-w-5xl mx-auto w-full'
      }
    >
      {visibleCards.map((share) => (
        <ScreenShareTile
          single={single}
          key={share.participantIdentity}
          share={share}
          sharerName={nameFor(share.participantIdentity)}
          sharerAvatarUrl={avatarFor(share.participantIdentity)}
          watchers={watchersFor(share.participantIdentity)}
          isLocal={share.participantIdentity === api.localParticipantIdentity}
          isPoppedOut={api.popoutShareIdentity === share.participantIdentity}
          onWatch={() => api.setShareSubscribed(share.participantIdentity, true)}
          onStopWatching={() => api.setShareSubscribed(share.participantIdentity, false)}
          onPopout={() => api.setPopoutShare(share.participantIdentity)}
          onClosePopout={() => api.setPopoutShare(null)}
          onSwitchSource={api.switchScreenShareSource}
        />
      ))}
    </div>
  );
}

function ScreenShareTile({
  single,
  share,
  sharerName,
  sharerAvatarUrl,
  watchers,
  isLocal,
  isPoppedOut,
  onWatch,
  onStopWatching,
  onPopout,
  onClosePopout,
  onSwitchSource,
}: {
  /** Sole share on the stage — fill the available height and scale the video
   *  to fit (so it shrinks reactively as the participant strip grows) rather
   *  than using a fixed max-height that would scroll. */
  single: boolean;
  share: VoiceApi['screenShares'][number];
  sharerName: string;
  sharerAvatarUrl: string | null;
  watchers: Array<{ identity: string; name: string; avatarUrl: string | null }>;
  isLocal: boolean;
  isPoppedOut: boolean;
  onWatch: () => void;
  onStopWatching: () => void;
  onPopout: () => void;
  onClosePopout: () => void;
  onSwitchSource: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // For local shares (you're the publisher): you don't strictly need to see
  // your own video — and the live thumbnail can be distracting, especially
  // when you're sharing a chat/IDE/whatever you actually want to look at
  // directly. Let the sharer collapse the preview to a compact status row.
  // Remote shares don't use this — they have onStopWatching to dismiss.
  const [hidePreview, setHidePreview] = useState(false);

  // Attach the LiveKit track to the video element when subscribed. Detach
  // on cleanup or when we unsubscribe.
  // CRITICAL: If this share is currently in the popout window, do NOT attach
  // here — that would result in two simultaneous video/audio outputs of the
  // same track. The popout is the canonical render in that case.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isPoppedOut) return; // popout owns the render
    if (!share.isSubscribed && !isLocal) return; // Local share is always playable
    const track = share.publication.track;
    if (!track) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
    // hidePreview is a dep because toggling it unmounts/remounts the <video>
    // element. The track reference is unchanged across that toggle, so without
    // hidePreview here the effect wouldn't re-run and the freshly-mounted
    // element would never get the track attached — leaving a black screen when
    // the local sharer clicks "Show preview" again.
  }, [share.publication, share.publication.track, share.isSubscribed, isLocal, isPoppedOut, hidePreview]);

  // Attach the share's audio track to a hidden <audio> element so listeners
  // can hear tab/system audio shared by the publisher. Skipped for local
  // (you don't want to hear your own shared audio fed back) and when the
  // share is popped out (popout owns audio playback in that case to avoid
  // duplicate playback).
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPoppedOut || isLocal) return;
    if (!share.isSubscribed) return;
    const audioTrack = share.audioPublication?.track;
    if (!audioTrack) return;
    audioTrack.attach(el);
    return () => {
      audioTrack.detach(el);
    };
  }, [share.audioPublication, share.audioPublication?.track, share.isSubscribed, isLocal, isPoppedOut]);

  // Apply the user's stored share-volume override.
  //
  // Important: setting `el.volume` alone is NOT enough. LiveKit's room has
  // `webAudioMix` enabled (see VoiceConnection.tsx around line 314), which
  // means remote audio is routed through a Web Audio GainNode internally,
  // not just through the <audio> element's playback level. When that's the
  // case, the only way to actually attenuate playback is to call
  // `RemoteAudioTrack.setVolume(...)` on the track — that adjusts the
  // GainNode's gain value.
  //
  // We set both for belt-and-suspenders:
  //   - track.setVolume() — primary path, controls the Web Audio gain
  //   - el.volume — fallback for any path that bypasses Web Audio
  // Together they guarantee 0 means silent.
  const shareVolumeStore = useApp((s) => s.screenShareVolumes[share.participantIdentity] ?? 1);
  useEffect(() => {
    const v = Math.max(0, Math.min(1, shareVolumeStore));
    const el = audioRef.current;
    if (el) el.volume = v;
    // Call setVolume on the audio track. The type guard checks for the
    // method (only RemoteAudioTrack has it, not LocalAudioTrack) so we
    // don't error on the local-share case.
    const track = share.audioPublication?.track as
      | { setVolume?: (v: number) => void }
      | undefined;
    if (track && typeof track.setVolume === 'function') {
      track.setVolume(v);
    }
  }, [shareVolumeStore, share.audioPublication?.track]);

  // For your own share, always show the video unless you've chosen to hide
  // the preview. For remote, show video only if subscribed AND not popped out.
  const showVideo = (isLocal || share.isSubscribed) && !isPoppedOut && !(isLocal && hidePreview);
  // Fill-to-height mode: only when this is the sole share AND we're showing the
  // live video. The card becomes a flex column and the video scales to fit the
  // remaining height (object-contain), so it shrinks reactively instead of
  // scrolling when the participant strip grows.
  const fill = single && showVideo;

  return (
    <div className={`hybrid-card ${fill ? 'flex flex-col min-h-0 flex-1' : ''}`}>
      <div className="hybrid-card-header flex-wrap gap-x-2 gap-y-1">
        <Icon name="monitor" size={11} />
        <span className="truncate flex-1 min-w-32">
          {isLocal ? 'You are sharing your screen' : `${sharerName} is sharing their screen`}
        </span>
        <WatcherIndicator watchers={watchers} isLocal={isLocal} />
        {share.audioPublication && !isLocal && share.isSubscribed && (
          <ShareAudioControl participantIdentity={share.participantIdentity} />
        )}
        {share.audioPublication && (isLocal || !share.isSubscribed) && (
          <span
            className="text-[10px] opacity-80"
            title={isLocal ? 'Sharing tab/system audio' : 'This share includes audio'}
          >
            <Icon name="speaker" size={11} />
          </span>
        )}
        {/* Local-share: swap what you're sharing without dropping the share —
            viewers, popout, and the watcher list all survive the switch. */}
        {isLocal && (
          <button
            type="button"
            onClick={onSwitchSource}
            className="text-[10px] hover:underline opacity-80 hover:opacity-100"
            title="Pick a different screen, window, or tab (your viewers keep watching)"
          >
            Switch source
          </button>
        )}
        {/* Local-share: hide/show your own preview (purely cosmetic; the
            actual share continues regardless). */}
        {isLocal && (
          <button
            type="button"
            onClick={() => setHidePreview((v) => !v)}
            className="text-[10px] hover:underline opacity-80 hover:opacity-100"
            title={hidePreview ? 'Show your own preview' : 'Hide your own preview (you keep sharing)'}
          >
            {hidePreview ? 'Show preview' : 'Hide preview'}
          </button>
        )}
        {!isLocal && share.isSubscribed && (
          <>
            {isPoppedOut ? (
              <button
                type="button"
                onClick={onClosePopout}
                className="text-[10px] hover:underline opacity-80 hover:opacity-100"
                title="Bring share back into the channel"
              >
                Bring back
              </button>
            ) : (
              <button
                type="button"
                onClick={onPopout}
                className="text-[10px] hover:underline opacity-80 hover:opacity-100"
                title="Pop out into floating window"
              >
                Pop out
              </button>
            )}
            <button
              type="button"
              onClick={onStopWatching}
              className="text-[10px] hover:underline opacity-80 hover:opacity-100"
            >
              Stop watching
            </button>
          </>
        )}
      </div>
      {isPoppedOut ? (
        <div className="bg-elevated rounded-b-aim p-6 flex flex-col items-center gap-3 text-text-muted">
          <Icon name="monitor" size={28} />
          <div className="text-sm text-center">
            Watching in floating window.
            <br />
            <span className="text-xs text-text-subtle">
              Click "Bring back" above to dock it here.
            </span>
          </div>
        </div>
      ) : showVideo ? (
        <div
          className={`bg-black rounded-b-aim overflow-hidden ${
            fill ? 'flex-1 min-h-0 flex items-center justify-center' : ''
          }`}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isLocal /* don't echo your own share's audio back to you */}
            className={
              fill
                ? 'max-h-full max-w-full object-contain bg-black'
                : 'w-full h-auto max-h-[60vh] block bg-black'
            }
          />
          {/* Hidden audio sink for screen-share tab/system audio. Always
              rendered when the video is, even if no audio publication is
              currently attached — the effect attaches it lazily. autoPlay so
              it starts as soon as the track is bound. */}
          <audio ref={audioRef} autoPlay />
        </div>
      ) : isLocal ? (
        // Local share with preview hidden. The share is still active —
        // remote viewers see it normally; we just collapsed the local
        // preview so the sharer can focus on what they're sharing.
        <div className="bg-elevated rounded-b-aim p-4 flex flex-col items-center gap-2 text-text-muted">
          <Icon name="monitor" size={20} />
          <div className="text-xs text-center">
            You're sharing your screen.
            <br />
            <span className="text-text-subtle">Preview hidden — click "Show preview" to see it.</span>
          </div>
        </div>
      ) : (
        // Prominent click-to-watch card. The whole body is a button (big hit
        // target), with an explicit "Watch screen" call-to-action inside. We
        // keep the share unsubscribed until clicked (bandwidth opt-in).
        <button
          type="button"
          onClick={onWatch}
          aria-label={`Watch ${sharerName}'s screen`}
          className="group/watch w-full bg-elevated hover:bg-elevated/70 rounded-b-aim px-4 py-3 flex items-center gap-3 text-text-muted transition-colors cursor-pointer text-left"
        >
          <Avatar name={sharerName} url={sharerAvatarUrl} size={36} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-text truncate">
              <strong>{sharerName}</strong> is sharing their screen
            </div>
            <div className="text-[10px] text-text-subtle">Loads on click to save bandwidth</div>
          </div>
          <span className="aim-button text-xs! px-3! py-1.5! flex items-center gap-1.5 shrink-0 group-hover/watch:bg-accent! group-hover/watch:text-white! transition-colors">
            <Icon name="monitor" size={14} />
            Watch
          </span>
        </button>
      )}
    </div>
  );
}

// WatcherIndicator — small "who's watching" badge for a screen-share tile.
// Shows up to three stacked avatars plus a head-count, with the full name list
// on hover. Renders nothing when nobody is watching. For your own share this is
// the answer to "who can see what I'm sharing right now?".
function WatcherIndicator({
  watchers,
  isLocal,
}: {
  watchers: Array<{ identity: string; name: string; avatarUrl: string | null }>;
  isLocal: boolean;
}) {
  if (watchers.length === 0) {
    // For your OWN share, always surface a status so you know — at a glance and
    // even before anyone tunes in — whether anybody is currently looking. For a
    // remote share an empty list just renders nothing (no clutter).
    if (!isLocal) return null;
    return (
      <span
        className="flex items-center gap-1 text-[10px] opacity-60 shrink-0"
        title="No one is watching your screen yet"
      >
        <Icon name="eye" size={11} />
        <span>No viewers yet</span>
      </span>
    );
  }
  const shown = watchers.slice(0, 3);
  const extra = watchers.length - shown.length;
  const names = watchers.map((w) => w.name).join(', ');
  return (
    <span
      className="flex items-center gap-1 text-[10px] opacity-90 shrink-0"
      title={`${isLocal ? 'Watching your screen' : 'Watching'}: ${names}`}
    >
      <Icon name="eye" size={11} />
      <span className="flex -space-x-1.5">
        {shown.map((w) => (
          <span key={w.identity} className="ring-1 ring-surface rounded-full">
            <Avatar name={w.name} url={w.avatarUrl} size={16} />
          </span>
        ))}
      </span>
      <span className="tabular-nums">{extra > 0 ? `+${extra}` : watchers.length}</span>
    </span>
  );
}

function parseMetadata(metadata?: string): { username?: string; displayName?: string; avatarUrl?: string | null } {
  if (!metadata) return {};
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
}

// =============================================================================
// ShareAudioControl — speaker-icon button + popover with volume slider.
// =============================================================================
// Sits in the title bar of a screen-share tile (and the popout). Shown only
// when the share has an audio publication and we're subscribed to it. Clicking
// the speaker toggles a small popover with a 0..100% slider. State persists
// in the global store via useVoice().setShareVolume.
//
// Used for both the in-channel tile and the popout — same component, just
// rendered in two places. The popover anchors itself to its trigger so it
// stays correctly positioned in either context.
// =============================================================================
function ShareAudioControl({ participantIdentity }: { participantIdentity: string }) {
  // Read/write the volume store DIRECTLY rather than through useVoice(). The
  // voice context value changes every time someone's speaking state flips (the
  // analyser/gate rings update several times a second while anyone is talking),
  // and a controlled <input type="range"> that re-renders mid-drag visibly
  // stutters — the thumb snapped between steps (the "95↔100" glitch) while the
  // sharer talked over their screen share. A narrow store subscription
  // re-renders this control ONLY when ITS own volume changes. The actual share
  // audio is applied from this same store key (see ScreenShareTile), so the
  // slider stays in lockstep with what's heard.
  const volume = useApp((s) => s.screenShareVolumes[participantIdentity] ?? 1);
  const setVolume = useApp((s) => s.setScreenShareVolume);
  const resetVolume = useApp((s) => s.resetScreenShareVolume);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const pct = Math.round(volume * 100);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (popRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Show a "muted" badge when volume is exactly 0, just to make it visible at
  // a glance that this share's audio is silenced.
  const isMuted = pct === 0;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`text-[10px] hover:opacity-100 transition-opacity flex items-center gap-0.5 px-1 py-0.5 rounded ${
          isMuted ? 'opacity-100 text-danger' : 'opacity-80'
        }`}
        title={`Share audio: ${pct}%`}
      >
        <Icon name="speaker" size={11} />
        {(isMuted || pct !== 100) && <span className="font-mono tabular-nums">{pct}</span>}
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute right-0 top-full mt-1 z-50 aim-window shadow-aim-window p-3 w-56"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-surface space-y-1">
            <div className="flex items-center justify-between text-text">
              <span className="text-[10px] uppercase tracking-wider text-text-subtle font-bold">
                Share audio
              </span>
              <span className="text-xs text-text-muted font-mono tabular-nums">{pct}%</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="speaker" size={12} className="text-text-subtle" />
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={pct}
                onChange={(e) => setVolume(participantIdentity, Number(e.target.value) / 100)}
                className="flex-1 accent-accent"
                aria-label="Share audio volume"
              />
              {/* Always rendered (visibility toggled, not mounted) so the
                  slider's width never changes as the value crosses 100% —
                  otherwise unmounting this mid-drag reflows the flex-1 track,
                  moving the thumb under the cursor and making the value
                  oscillate near the top of the range. */}
              <button
                type="button"
                onClick={() => resetVolume(participantIdentity)}
                className="text-[10px] text-text-subtle hover:text-text px-1"
                title="Reset to 100%"
                style={{ visibility: pct === 100 ? 'hidden' : 'visible' }}
                disabled={pct === 100}
                tabIndex={pct === 100 ? -1 : 0}
                aria-hidden={pct === 100}
              >
                reset
              </button>
            </div>
            <div className="text-[10px] text-text-subtle">
              Affects only what you hear. Your preference is saved.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
